import React from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Loader2, Shield, Clock } from 'lucide-react';

// ─────────── Audit Activity Panel ───────────

const ACTION_META = {
  'pro.grant':         { label: 'Granted Pro',        tone: 'emerald' },
  'pro.revoke':        { label: 'Revoked Pro',        tone: 'rose'    },
  'affiliate.grant':   { label: 'Granted affiliate',  tone: 'emerald' },
  'affiliate.revoke':  { label: 'Revoked affiliate',  tone: 'rose'    },
  'affiliate.approve': { label: 'Approved affiliate', tone: 'emerald' },
  'affiliate.reject':  { label: 'Rejected affiliate', tone: 'rose'    },
  'payout.mark_paid':  { label: 'Marked payout paid', tone: 'sky'     },
};

const TONE_CLASSES = {
  emerald: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  rose:    'bg-rose-500/10 text-rose-600 border-rose-500/30',
  sky:     'bg-sky-500/10 text-sky-600 border-sky-500/30',
  muted:   'bg-muted/40 text-muted-foreground border-border',
};

const _fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IE', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

export const AuditPanel = ({ auditLog, loading, onRefresh }) => {
  return (
    <div className="lm-enter" data-testid="audit-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold" data-testid="audit-heading">Activity log</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Every privileged admin action, immutable. Most recent first.</p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="refresh-audit-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
        </Button>
      </div>

      {loading && !auditLog ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : !auditLog || auditLog.entries.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground" data-testid="audit-empty">
          <Shield className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No admin actions logged yet.</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <Card className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Total events</p>
              <p className="text-2xl font-semibold tracking-tight" data-testid="audit-total">{auditLog.total}</p>
            </Card>
            <Card className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Pro grants</p>
              <p className="text-2xl font-semibold tracking-tight text-emerald-600">
                {auditLog.entries.filter(e => e.action === 'pro.grant').length}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Payouts</p>
              <p className="text-2xl font-semibold tracking-tight text-sky-600">
                {auditLog.entries.filter(e => e.action === 'payout.mark_paid').length}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Affiliate changes</p>
              <p className="text-2xl font-semibold tracking-tight">
                {auditLog.entries.filter(e => e.action.startsWith('affiliate.')).length}
              </p>
            </Card>
          </div>

          <div className="space-y-2" data-testid="audit-list">
            {auditLog.entries.map((e, i) => {
              const meta = ACTION_META[e.action] || { label: e.action, tone: 'muted' };
              const actor = e.actor_username || e.actor_id?.slice(-6) || 'unknown';
              const target = e.metadata?.username
                ? `@${e.metadata.username}`
                : e.target_id ? `${e.target_type || 'id'}:${e.target_id.slice(-6)}` : '';
              const detail = [];
              if (e.metadata?.cycle) detail.push(e.metadata.cycle);
              if (e.metadata?.amount) detail.push(`€${Number(e.metadata.amount).toFixed(2)}`);
              if (e.metadata?.commission_count) detail.push(`${e.metadata.commission_count} commission(s)`);
              if (e.metadata?.previous_cycle) detail.push(`was ${e.metadata.previous_cycle}`);
              return (
                <Card key={`${e.created_at}-${i}`} className="p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`audit-row-${i}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Badge variant="outline" className={`text-[10px] font-medium ${TONE_CLASSES[meta.tone]} flex-shrink-0`}>
                      {meta.label}
                    </Badge>
                    <div className="min-w-0">
                      <p className="text-sm">
                        <span className="font-semibold">@{actor}</span>
                        {target && <> → <span className="font-medium">{target}</span></>}
                        {detail.length > 0 && <span className="text-muted-foreground"> · {detail.join(' · ')}</span>}
                      </p>
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Clock className="w-3 h-3" /> {_fmtDateTime(e.created_at)}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          <p className="text-xs text-muted-foreground mt-6">Showing latest {auditLog.entries.length} of {auditLog.total} entries.</p>
        </>
      )}
    </div>
  );
};
